import React, { useEffect, useState } from "react";
import { View, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Input from "components/Input";
import Empty from "components/Empty";
import Error from "components/Error";
import Loading from "components/Loading";
import ModalInfinityList from "components/ModalInfinityList";
import AnimatedInfinityList from "components/AnimatedInfinityList";

import { styles } from "./styles";
import { sizes, colors, strings } from "assets/constants";

import { useFlag } from "hooks";
import { liveSearchInput, mq } from "utils";

import { useProvinces } from "clients/apis";

let timer: ReturnType<typeof setTimeout>;

export default function ProvincePicker(props: PickerT) {
  const { id, value, onSelect, error, hideError, onPress } = props;
  const val = value as LocationT;

  const [name, setName] = useState("");
  const {
    isError,
    error: provincesError,
    isRefetching,
    refetch,
    isLoading: provincesFetching,
    data: provinces,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProvinces({ name, id });

  const { flg: modal, on: show, off: hide } = useFlag();
  const { flg: typing, on: startTyping, off: stopTyping } = useFlag();

  const _onHide = () => {
    setName("");
    hide();
  };

  const [valueState, setValueState] = useState<LocationT | undefined>();

  useEffect(() => {
    setValueState(val);
  }, [val]);

  const _renderItem = (items: []) => {
    return items.map((province: LocationT, key: number) => (
      <React.Fragment key={String(key)}>
        <TouchableOpacity
          style={styles.item}
          onPress={() => {
            onSelect && onSelect(province);
            setName("");
            setValueState(province);
            hide();
          }}
        >
          <Text>{province.text}</Text>
        </TouchableOpacity>
        <View style={styles.itemSeparator} />
      </React.Fragment>
    ));
  };

  const _renderList = () => {
    if (provincesFetching || typing) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return (
        <Error error={provincesError} reset={refetch} loading={isRefetching} />
      );
    }

    if (provinces?.pages?.[0]?.data?.data?.length === 0) {
      return <Empty message={strings.no_province} />;
    }

    return (
      <AnimatedInfinityList
        onRefresh={refetch}
        responseData={provinces}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        renderItemList={_renderItem}
      />
    );
  };

  return (
    <>
      <>
        <TouchableOpacity
          onPress={onPress ?? show}
          activeOpacity={sizes.tapOpacity}
          style={{ ...styles.container }}
        >
          <View style={styles.iconContainer}>
            <Icon name="chevron-down" />
          </View>
          <View style={styles.textContainer}>
            <Text
              color={
                valueState?.text ? colors.textColorDefault : colors.placeholder
              }
            >
              {valueState?.text || strings.pick_province}
            </Text>
          </View>

          <View style={styles.iconContainer}>
            <Icon name="map-pin" />
          </View>
        </TouchableOpacity>

        {!hideError && (
          <View style={styles.errorContainer}>
            <Text size={"xs"} color={colors.danger}>
              {error}
            </Text>
          </View>
        )}
      </>

      <ModalInfinityList visible={modal} close={_onHide}>
        <View style={styles.listSearchContainer}>
          <Input
            hideError
            icon={{ name: "search" }}
            placeholder={strings.search}
            onChangeText={(inputName) =>
              liveSearchInput(
                inputName,
                name,
                setName,
                timer,
                startTyping,
                stopTyping
              )
            }
          />
        </View>
        {_renderList()}
      </ModalInfinityList>
    </>
  );
}
